import { niceScale } from "./chartScale";
import type { DayCount } from "./progressTypes";

// DailyBarChart is a compact vertical stacked bar chart (no charting library —
// same hand-rolled approach as LabelledBarChart) for up to three 30-day daily
// series: commits, merged pull requests and closed issues. Each day is one
// column with commits at the base, merges above and issues closed on top —
// real user request, replacing the earlier three-bars-side-by-side layout,
// which at 30 days wide left each bar a sliver. Issues closed is the measure
// of work actually finished on this programme, much of which is tracked as
// issues rather than as pull requests. Unlike LabelledBarChart's horizontal
// per-row layout (fine for a handful of months), 30 individually labelled
// rows would be unreadably long, so this grows columns upward instead — a
// title tooltip per column carries the date and every series' count rather
// than labelling every bar, with the first/last dates and running totals
// anchoring the axis.
//
// The value axis (gridlines plus their labels, see chartScale.niceScale)
// is what lets a reader take a number off the chart. Because the column is
// the stack, the axis is scaled to the largest daily *total*, and every
// segment is sized against that one scale — segment heights add up to the
// column's height, so the top of each column reads as that day's total.
//
// Each series is looked up by date rather than zipped by index: the arrays
// come from independently-fetched, independently-failable sections (see
// internal/progress's CommitActivitySection/MergeActivitySection/
// IssueActivitySection), so any one can arrive empty while the others
// don't — the date spine falls back to whichever series is non-empty, and
// issues is optional so a failed issue fetch drops its segment rather than
// the chart.
function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

export function DailyBarChart({
  commits,
  merges,
  issues = [],
}: {
  commits: DayCount[];
  merges: DayCount[];
  issues?: DayCount[];
}) {
  const commitsByDate = new Map(commits.map((d) => [d.date, d.count]));
  const mergesByDate = new Map(merges.map((d) => [d.date, d.count]));
  const issuesByDate = new Map(issues.map((d) => [d.date, d.count]));
  const spine = [commits, merges, issues].find((s) => s.length > 0) ?? [];
  const showIssues = issues.length > 0;
  const days = spine.map(({ date }) => {
    const commitCount = commitsByDate.get(date) ?? 0;
    const mergeCount = mergesByDate.get(date) ?? 0;
    const issueCount = showIssues ? (issuesByDate.get(date) ?? 0) : 0;
    return { date, commitCount, mergeCount, issueCount, total: commitCount + mergeCount + issueCount };
  });
  const { max, ticks } = niceScale(Math.max(0, ...days.map((d) => d.total)));
  const totalCommits = commits.reduce((sum, d) => sum + d.count, 0);
  const totalMerges = merges.reduce((sum, d) => sum + d.count, 0);
  const totalIssues = issues.reduce((sum, d) => sum + d.count, 0);
  const pct = (n: number) => `${(n / max) * 100}%`;

  return (
    <div className="day-bar-chart">
      <div
        className="day-bar-track"
        role="img"
        aria-label={`Daily activity over the last ${days.length} days: ${totalCommits} commits, ${totalMerges} merges${showIssues ? `, ${totalIssues} issues closed` : ""}`}
      >
        {[...ticks].reverse().map((t) => (
          <div className="chart-gridline" key={t} style={{ bottom: `${(t / max) * 100}%` }}>
            <span className="chart-gridline-label">{t}</span>
          </div>
        ))}
        {days.map(({ date, commitCount, mergeCount, issueCount, total }) => (
          <div
            key={date}
            className="day-bar-stack"
            title={`${date}: ${plural(commitCount, "commit")}, ${plural(mergeCount, "merge")}${
              showIssues ? `, ${plural(issueCount, "issue")} closed` : ""
            } (${total} total)`}
          >
            <div className="day-bar day-bar-commits" style={{ height: pct(commitCount) }} />
            <div className="day-bar day-bar-merges" style={{ height: pct(mergeCount) }} />
            {showIssues && <div className="day-bar day-bar-issues" style={{ height: pct(issueCount) }} />}
          </div>
        ))}
      </div>
      <div className="day-bar-axis">
        <span>{days[0]?.date}</span>
        <span className="day-bar-total">
          {totalCommits} commits · {totalMerges} merges{showIssues ? ` · ${totalIssues} issues closed` : ""}
        </span>
        <span>{days[days.length - 1]?.date}</span>
      </div>
      <div className="day-bar-legend">
        <span className="day-bar-legend-item">
          <span className="day-bar-swatch day-bar-swatch-commits" /> commits
        </span>
        <span className="day-bar-legend-item">
          <span className="day-bar-swatch day-bar-swatch-merges" /> merges
        </span>
        {showIssues && (
          <span className="day-bar-legend-item">
            <span className="day-bar-swatch day-bar-swatch-issues" /> issues closed
          </span>
        )}
      </div>
    </div>
  );
}
